import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Store, MapPin, Mail, Phone, CreditCard, MessageCircle, ShieldCheck, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../../components/Navbar";
import { formatApiError } from "../../lib/api";
import { mk, photoUrl, priceLabel, money } from "../../lib/market";
import { EmailGate } from "../../components/market/EmailGate";
import { useMarketMe } from "./MarketHome";
import { SaveButton } from "../../components/market/SaveButton";
import { CartButton } from "../../components/market/CartButton";

function Gallery({ photos }) {
  const [i, setI] = useState(0);
  return (
    <div>
      <div className="aspect-[4/5] rounded-3xl overflow-hidden bg-[var(--bg-warm)]"><img src={photoUrl(photos[i])} alt="" className="w-full h-full object-cover" data-testid="item-photo-main" /></div>
      {photos.length > 1 && <div className="flex gap-2 mt-3">{photos.map((p, k) => <button key={p} onClick={() => setI(k)} className={`w-16 h-16 rounded-xl overflow-hidden border-2 ${k === i ? "border-[var(--gold)]" : "border-transparent"}`} data-testid={`item-thumb-${k}`}><img src={photoUrl(p)} alt="" className="w-full h-full object-cover" /></button>)}</div>}
    </div>
  );
}

function Contact({ l }) {
  const [msg, setMsg] = useState(l.kind === "free" ? "Hi! I'd love this donation. When could I pick it up?" : "Hi! Is this still available?");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  async function send() {
    setBusy(true);
    try { const { data } = await mk.post(`/market/listings/${l.listing_id}/messages`, { text: msg }); toast.success("Message sent"); nav(`/market/inbox?t=${data.thread_id}`); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function buy() {
    setBusy(true);
    try { const { data } = await mk.post(`/market/listings/${l.listing_id}/checkout`, { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  }
  async function addToCart() {
    setBusy(true);
    try {
      await mk.post("/market/cart/items", { listing_id: l.listing_id });
      window.dispatchEvent(new Event("lif-cart"));
      toast.success("Added to your cart", { action: { label: "View cart", onClick: () => nav("/market/cart") } });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  if (l.is_mine) return <Link to="/market/sell" className="btn-outline-gold w-full" data-testid="item-manage">Manage this listing</Link>;
  const open = l.status === "active";
  return (
    <div className="space-y-3">
      {!open && <p className="font-serif text-2xl text-[var(--muted)]" data-testid="item-sold">This item has found its new home.</p>}
      {open && l.kind === "sale" && l.buy_now && <button onClick={buy} disabled={busy} className="btn-gold w-full" data-testid="item-buy-now"><CreditCard className="w-4 h-4" /> Buy now · {money(l.price)}</button>}
      {open && l.kind === "sale" && l.buy_now && <button onClick={addToCart} disabled={busy} className="btn-outline-gold w-full" data-testid="item-add-to-cart"><ShoppingBag className="w-4 h-4" /> Add to cart</button>}
      <SaveButton id={l.listing_id} big />
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} maxLength={2000} className="input-luxe" data-testid="item-message-input" />
      <button onClick={send} disabled={busy || !msg.trim()} className="btn-outline-gold w-full" data-testid="item-message-send"><MessageCircle className="w-4 h-4" /> Message {l.seller_type === "store" ? "the store" : "the seller"}</button>
      {(l.contact_email || l.contact_phone) && (
        <div className="text-sm space-y-1 pt-2" data-testid="item-contact">
          {l.contact_email && <a href={`mailto:${l.contact_email}`} className="flex items-center gap-2 hover:text-[var(--gold)]"><Mail className="w-4 h-4" /> {l.contact_email}</a>}
          {l.contact_phone && <a href={`tel:${l.contact_phone}`} className="flex items-center gap-2 hover:text-[var(--gold)]"><Phone className="w-4 h-4" /> {l.contact_phone}</a>}
        </div>
      )}
      {l.kind === "sale" && l.buy_now && <p className="text-[11px] text-[var(--muted)] flex items-start gap-1.5"><ShieldCheck className="w-3.5 h-3.5 shrink-0 mt-0.5" /> Card payments are held by Locked In Forever and paid to the seller after the sale. Arrange pickup or shipping in messages.</p>}
    </div>
  );
}

export default function MarketItem() {
  const { id } = useParams();
  const [me, reloadMe] = useMarketMe();
  const [l, setL] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { if (me !== undefined) mk.get(`/market/listings/${id}`).then((r) => setL(r.data)).catch((e) => setErr(formatApiError(e))); }, [id, me]);
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <Navbar />
      <div className="container-wide px-5 md:px-12 pt-28 pb-20">
        <div className="flex items-center justify-between mb-6"><Link to="/market" className="text-sm text-[var(--muted)] flex items-center gap-1" data-testid="item-back"><ArrowLeft className="w-4 h-4" /> Marketplace</Link>{me && <CartButton />}</div>
        {err && <p className="font-serif text-3xl text-[var(--muted)]" data-testid="item-error">{err}</p>}
        {l && (
          <div className="grid md:grid-cols-2 gap-10 lg:gap-16" data-testid="market-item">
            <Gallery photos={l.photos} />
            <div>
              <p className="text-[11px] uppercase tracking-[0.25em] text-[var(--gold)]">{l.category} · {l.condition}</p>
              <h1 className="font-serif font-light text-4xl md:text-5xl mt-2" data-testid="item-title">{l.title}</h1>
              <p className="text-3xl mt-4" data-testid="item-price">{priceLabel(l)}</p>
              {l.kind === "rental" && l.deposit > 0 && <p className="text-sm text-[var(--muted)]">Refundable deposit {money(l.deposit)}</p>}
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-sm text-[var(--muted)] mt-4">
                {l.size && <span>Size {l.size}</span>}
                {l.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {l.location}</span>}
                <span className="flex items-center gap-1" data-testid="item-seller">{l.seller_type === "store" && <Store className="w-3.5 h-3.5 text-[var(--gold)]" />}{l.seller_name}{l.seller_type === "store" && " · Bridal store"}</span>
              </div>
              {l.description && <p className="mt-6 whitespace-pre-line leading-relaxed">{l.description}</p>}
              <div className="mt-8 max-w-md">{me ? <Contact l={l} /> : <div data-testid="item-checkout-gate"><p className="text-sm text-[var(--muted)] mb-3">Confirm your email to buy, rent or message the seller.</p><EmailGate onDone={reloadMe} /></div>}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
